import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { devMode } from '@/auth/devMode';
import { signOut } from '@/auth/autoSignIn';
import { matchNav, visibleSections } from '@/components/nav';
import { ThemeToggle } from '@/components/ThemeToggle';
import type { Me } from '@/types';

export function AppBar({ me }: { me: Me }) {
  const initials = me.displayName.split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const [navOpen, setNavOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  const sections = visibleSections({ devMode, isAdmin: me.isAdmin, features: me.features });
  const { section, item } = matchNav(pathname);

  // Only offer the second tier for a section this caller can actually see. matchNav answers from
  // the whole nav model on purpose — it is also what breadcrumbs use, and a path has to resolve to
  // its section whether or not the section is on this person's bar. Taking its children unfiltered
  // meant a hidden section still published its sub-pages: with the feature switched off, landing on
  // /admin/packs showed "Pack library" and "New pack" as tabs under a section the bar had removed.
  const subItems = section && sections.includes(section) ? section.children ?? [] : [];

  /*
   * Both tiers take their highlight from the nav model rather than from
   * NavLink's own path matching, because section membership does not follow the
   * path: NavLink would leave "Subscriptions" unlit on /history, and would light
   * "My subscriptions" on /subscriptions/new.
   *
   * aria-current is "page" only for a link that really is this page, and "true"
   * for one that is merely the branch you are in — a section link, or "My
   * subscriptions" while you are inside a subscription.
   */
  const current = (to: string, active: boolean) =>
    (to === pathname ? 'page' : active ? true : undefined);

  // Below the nav breakpoint the primary nav is an overlay panel, and React
  // Router will not unmount it on navigation — so following a link inside it
  // has to close it explicitly.
  useEffect(() => { setNavOpen(false); }, [pathname]);

  useEffect(() => {
    if (!navOpen) return;

    // Escape closes and hands focus back to the control that opened it. There is
    // deliberately no focus trap: the panel sits in the page rather than over it,
    // and tabbing past its last link should reach sign-out, not loop.
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setNavOpen(false);
      toggleRef.current?.focus();
    }
    function onPointerDown(e: PointerEvent) {
      if (!headerRef.current?.contains(e.target as Node)) setNavOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [navOpen]);

  return (
    <>
      <header className="app-bar" ref={headerRef}>
        <div className="brand">
          {/* Initials of the product, not information — the name is right beside it. */}
          <div className="logo" aria-hidden="true">KT</div>
          <span>Reporting</span>
        </div>

        <button
          type="button"
          className="nav-toggle"
          ref={toggleRef}
          aria-label="Main menu"
          aria-expanded={navOpen}
          aria-controls="primary-nav"
          onClick={() => setNavOpen(open => !open)}>
          <span className="nav-toggle-bars" aria-hidden="true" />
        </button>

        <nav
          id="primary-nav"
          aria-label="Primary"
          className={navOpen ? 'primary-nav open' : 'primary-nav'}>
          {sections.map(s => {
            const active = s.key === section?.key;
            return (
              <Link key={s.key} to={s.to} className={active ? 'active' : ''} aria-current={current(s.to, active)}>
                {s.label}
              </Link>
            );
          })}
        </nav>

        <div className="user-menu">
          {/* First in the group, so it keeps its place when the name and role are
              clipped out of the layout at 1024px and the avatar slides left. */}
          <ThemeToggle />
          {/* aria-hidden: two letters of a name that is spelled out next to them. */}
          <div className="avatar" aria-hidden="true">{initials}</div>
          {/* Narrow widths hide this visually but keep it readable — see the
              1024px breakpoint in theme.css. */}
          <div className="user-identity">
            <div className="user-name">{me.displayName}</div>
            <div className="user-role">{me.isAdmin ? 'Administrator' : 'Account Manager'}</div>
          </div>
          {!devMode && <SignOutButton />}
        </div>
      </header>

      {/* Second tier. Rendered only for a section that has one, and labelled with
          the section's name so a screen reader hears which set of pages it is
          moving through rather than a second unnamed "navigation". */}
      {section && subItems.length > 0 && (
        <nav className="sub-nav" aria-label={section.label}>
          <div className="sub-nav-inner">
            {subItems.map(child => {
              const active = child.to === item?.to;
              return (
                <Link
                  key={child.to}
                  to={child.to}
                  className={active ? 'active' : ''}
                  aria-current={current(child.to, active)}>
                  {child.label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
}

/**
 * Rendered only outside dev mode, where main.tsx puts an MsalProvider above the
 * app. Calling useMsal without one throws, so the check has to happen before
 * this component is created rather than inside it.
 */
function SignOutButton() {
  const { instance } = useMsal();
  return (
    <button className="btn-ghost btn-sm btn-on-dark" onClick={() => signOut(instance)}>
      Sign out
    </button>
  );
}
